/**
 * URL Validation Utility
 * Provides security validation for return URLs to prevent open redirect vulnerabilities
 */

/**
 * Validates that a return URL is safe to redirect to
 * Prevents open redirect attacks by ensuring URLs are internal to the application
 *
 * @param url - The URL to validate
 * @returns true if URL is safe, false otherwise
 */
export const validateReturnUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Remove leading/trailing whitespace
  const trimmedUrl = url.trim();

  // Empty URLs are invalid
  if (trimmedUrl.length === 0) {
    return false;
  }

  // Whitelist of allowed routes (relative paths only)
  const allowedRoutes = [
    '/request-detail',
    '/create-request',
    '/view-requests',
    '/request',
    '/role-management',
  ];

  try {
    // Check if it's a relative path (starts with /)
    if (trimmedUrl.startsWith('/')) {
      // Extract pathname without query parameters
      const pathname = trimmedUrl.split('?')[0];

      // Check if pathname matches any allowed route
      const isAllowed = allowedRoutes.some(
        route => pathname === route || pathname.startsWith(route + '/'),
      );

      if (!isAllowed) {
        console.warn(
          '⚠️ Return URL validation failed: path not in whitelist:',
          pathname,
        );
        return false;
      }

      // Check for suspicious patterns
      if (
        trimmedUrl.includes('//') ||
        trimmedUrl.includes('\\') ||
        trimmedUrl.includes('javascript:') ||
        trimmedUrl.includes('data:')
      ) {
        console.warn(
          '⚠️ Return URL validation failed: suspicious pattern detected',
        );
        return false;
      }

      return true;
    }

    // Check if it's a full URL
    const urlObj = new URL(trimmedUrl);

    // For full URLs, validate that the origin matches the current origin (web only)
    if (typeof window !== 'undefined') {
      const currentOrigin = window.location.origin;
      if (urlObj.origin !== currentOrigin) {
        console.warn('⚠️ Return URL validation failed: origin mismatch', {
          expected: currentOrigin,
          received: urlObj.origin,
        });
        return false;
      }

      // Validate pathname is in whitelist
      const isAllowed = allowedRoutes.some(
        route =>
          urlObj.pathname === route || urlObj.pathname.startsWith(route + '/'),
      );

      if (!isAllowed) {
        console.warn(
          '⚠️ Return URL validation failed: path not in whitelist:',
          urlObj.pathname,
        );
        return false;
      }

      return true;
    }

    // For native platforms, only allow relative paths for safety
    console.warn(
      '⚠️ Return URL validation failed: full URLs not allowed on native platforms',
    );
    return false;
  } catch (error) {
    // URL parsing failed - likely not a valid URL
    console.warn('⚠️ Return URL validation failed: invalid URL format', error);
    return false;
  }
};

/**
 * Validates that a server-supplied redirect URL points to the expected Okta domain
 * over HTTPS before it is assigned to `window.location.href`.
 *
 * Prevents an open redirect (CWE-601): even though the URL normally comes from our
 * own trusted backend, the client should never blindly navigate to a value from a
 * network response without checking it lands on the domain we expect (defense in
 * depth against a compromised/misconfigured server response or a MITM tampering
 * with the payload).
 *
 * @param url - The redirect URL supplied by the server response.
 * @param oktaDomain - The expected Okta domain (e.g. `REACT_APP_OKTA_DOMAIN`).
 * @returns true if the URL is safe to assign to `window.location.href`.
 */
export const isValidOktaRedirectUrl = (
  url: string,
  oktaDomain: string,
): boolean => {
  if (
    !url ||
    typeof url !== 'string' ||
    !oktaDomain ||
    typeof oktaDomain !== 'string'
  ) {
    return false;
  }

  try {
    const parsed = new URL(url);

    if (parsed.protocol !== 'https:') {
      console.warn(
        '⚠️ Okta redirect URL validation failed: non-HTTPS protocol',
        parsed.protocol,
      );
      return false;
    }

    const expectedHost = oktaDomain.trim().toLowerCase();
    const actualHost = parsed.hostname.toLowerCase();

    if (!expectedHost || actualHost !== expectedHost) {
      console.warn(
        '⚠️ Okta redirect URL validation failed: hostname mismatch',
        {
          expected: expectedHost,
          received: actualHost,
        },
      );
      return false;
    }

    return true;
  } catch (error) {
    console.warn(
      '⚠️ Okta redirect URL validation failed: invalid URL format',
      error,
    );
    return false;
  }
};

/**
 * Sanitizes a return URL by removing any potentially dangerous components
 *
 * @param url - The URL to sanitize
 * @returns Sanitized URL or null if URL is invalid
 */
export const sanitizeReturnUrl = (url: string): string | null => {
  if (!validateReturnUrl(url)) {
    return null;
  }

  try {
    // For relative paths, just trim whitespace
    if (url.startsWith('/')) {
      return url.trim();
    }

    // For full URLs, extract just the pathname and search
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.search;
  } catch (error) {
    console.error('Failed to sanitize return URL:', error);
    return null;
  }
};

/**
 * Encodes a return URL for safe inclusion in query parameters
 *
 * @param url - The URL to encode
 * @returns Base64-encoded URL
 */
export const encodeReturnUrl = (url: string): string => {
  try {
    // Use base64 encoding for safe URL parameter transmission
    if (typeof btoa !== 'undefined') {
      return btoa(url);
    }
    // Fallback for environments without btoa (unlikely in modern RN/web)
    return encodeURIComponent(url);
  } catch (error) {
    console.error('Failed to encode return URL:', error);
    return encodeURIComponent(url);
  }
};

/**
 * Decodes a return URL from query parameters
 *
 * @param encodedUrl - The encoded URL to decode
 * @returns Decoded URL or null if decoding fails
 */
export const decodeReturnUrl = (encodedUrl: string): string | null => {
  try {
    // Try base64 decoding first
    if (typeof atob !== 'undefined') {
      return atob(encodedUrl);
    }
    // Fallback to URI decoding
    return decodeURIComponent(encodedUrl);
  } catch (error) {
    console.error('Failed to decode return URL:', error);
    return null;
  }
};
